import React from 'react';

import styled from '@emotion/styled';
import { Trans } from '@lingui/macro';

import { BREAKPOINTS, disabledColor, unit, white } from '@zego/theme';
import { chunkArray } from '@zego/utils';

import H1 from '@zego/components/atoms/H1';
import RegularText from '@zego/components/atoms/RegularText';
import View from '@zego/components/atoms/View';
import ContentContainer from '@zego/components/ContentContainer';
import SectionDisplay from '@zego/components/SectionDisplay';
import SmallHeaderText from '@zego/components/SmallHeaderText';

import Choice from '@zego/static/svgs/Choice.svg';
import CustomerService from '@zego/static/svgs/CustomerService.svg';
import HiddenFees from '@zego/static/svgs/HiddenFees.svg';
import Hourly from '@zego/static/svgs/Hourly.svg';
import NewProducts from '@zego/static/svgs/NewProducts.svg';
import Paper from '@zego/static/svgs/Paper.svg';

import Backdrop from './Backdrop.svg';

const Icon = styled('img')({
  width: 61,
  height: 61,
  display: 'block',
});

const ItemRow = styled(View)({
  display: 'flex',
  flexDirection: 'row',
});

const ItemContainer = styled(View)({
  marginBottom: unit(8),
  flex: 1,
  '&:first-of-type': {
    marginRight: unit(4),
  },
  [BREAKPOINTS.medium]: {
    flex: 1,
    '&:first-of-type': {
      marginRight: unit(11),
    },
  },
});

const IconContainer = styled(View)({
  marginBottom: unit(3),
});

const HeaderText = styled(SmallHeaderText)({
  marginBottom: unit(2),
});

const DescriptionText = styled(RegularText)({
  fontSize: 16,
  color: disabledColor(7),
  marginBottom: unit(2),
});

const SubTitle = styled(RegularText)({
  display: 'none',
  [BREAKPOINTS.medium]: {
    display: 'block',
    marginTop: unit(6),
    fontSize: 20,
    lineHeight: '26px',
    color: disabledColor(7),
  },
});

const WhyZego = () => {
  const REASONS = [
    {
      title: (
        <Trans id="Why Zego / Benefits / Paperless / Heading">Paperless</Trans>
      ),
      description: (
        <Trans id="Why Zego / Benefits / Paperless / Description">
          Never lose your important documents! View or download them in the app
          whenever you want.
        </Trans>
      ),
      icon: Paper,
    },
    {
      title: (
        <Trans id="Why Zego / Benefits / Customer Service / Heading">
          Friendly customer service
        </Trans>
      ),
      description: (
        <Trans id="Why Zego / Benefits / Customer Service / Description">
          Our team are available to answer your call or talk online seven days
          a week.
        </Trans>
      ),
      icon: CustomerService,
    },
    {
      title: (
        <Trans id="Why Zego / Benefits / Flexible / Heading">Flexible</Trans>
      ),
      description: (
        <Trans id="Why Zego / Benefits / Flexible / Description">
          We create policies to suit your needs, giving you the same
          flexibility you get from your work.
        </Trans>
      ),
      icon: Choice,
    },
    {
      title: (
        <Trans id="Why Zego / Benefits / No Hidden Fees / Heading">
          No hidden admin fees
        </Trans>
      ),
      description: (
        <Trans id="Why Zego / Benefits / No Hidden Fees / Description">
          We don’t charge for changing your address or vehicle details.
        </Trans>
      ),
      icon: HiddenFees,
    },
    {
      title: (
        <Trans id="Why Zego / Benefits / Available 24/7 / Heading">
          Available 24/7
        </Trans>
      ),
      description: (
        <Trans id="Why Zego / Benefits / Available 24/7 / Description">
          We’re always available via our app, meaning you can check your
          account wherever, whenever.
        </Trans>
      ),
      icon: Hourly,
    },
    {
      title: (
        <Trans id="Why Zego / Benefits / New Products / Heading">
          New products
        </Trans>
      ),
      description: (
        <Trans id="Why Zego / Benefits / New Products / Description">
          We add new products all the time, so if you have a suggestion we’d
          love to hear it.
        </Trans>
      ),
      icon: NewProducts,
    },
  ];

  return (
    <SectionDisplay
      css={{
        backgroundColor: disabledColor(2),
        paddingBottom: 0,
        [BREAKPOINTS.contentContainer]: {
          paddingBottom: 0,
        },
      }}
    >
      <div
        id="why-zego"
        css={{
          backgroundColor: white,
          backgroundPosition: 'right 0px top 0px',
          [BREAKPOINTS.contentContainer]: {
            backgroundSize: '110%',
            backgroundImage: `url(${Backdrop})`,
            backgroundRepeat: 'no-repeat',
            paddingBottom: unit(5),
            [BREAKPOINTS.contentContainer]: {
              paddingBottom: unit(10),
            },
          },
        }}
      >
        <ContentContainer
          css={{
            paddingTop: unit(4),
            paddingBottom: unit(4),
            [BREAKPOINTS.medium]: {
              display: 'flex',
              flexDirection: 'row',
            },
            [BREAKPOINTS.contentContainer]: {
              paddingTop: unit(16),
              paddingBottom: 0,
            },
          }}
        >
          <View
            css={{
              [BREAKPOINTS.medium]: {
                flex: 1,
                marginRight: unit(11),
                marginBottom: 0,
              },
            }}
          >
            <H1>
              <Trans id="Why Zego / Heading">Why choose Zego?</Trans>
            </H1>
            <SubTitle>
              <Trans id="Why Zego / Subheading">
                From our paperless approach, to the fact you have your
                insurance in your pocket 24/7, there’s plenty of benefits to
                joining Zego.
              </Trans>
            </SubTitle>
          </View>

          <View
            css={{
              paddingTop: unit(8),
              [BREAKPOINTS.medium]: { flex: 1.5, paddingTop: 0 },
            }}
          >
            {chunkArray(REASONS, 2).map((row, index) => (
              <ItemRow key={index}>
                {row.map((reason, rowIndex) => (
                  <ItemContainer key={rowIndex}>
                    <IconContainer>
                      <Icon src={reason.icon} alt="" />
                    </IconContainer>
                    <HeaderText>{reason.title}</HeaderText>
                    <DescriptionText>{reason.description}</DescriptionText>
                  </ItemContainer>
                ))}
              </ItemRow>
            ))}
          </View>
        </ContentContainer>
      </div>
    </SectionDisplay>
  );
};

export default WhyZego;
